import type { ConflictApiError, ConflictingElementInfo } from '@empathyco/playboard-core-domain'
import type { ComputedRef } from 'vue'
import type { ConflictErrorModalMessages } from '../../../models'
import type { ConflictDisplayData } from './types'
import { computed, getCurrentInstance } from 'vue'
import { ArrayUtils } from '../../../utils'

interface UseConflictErrorsProps {
  conflictApiError: ConflictApiError
  messages: ConflictErrorModalMessages['dialog']
  itemQueries: string[]
  updateRouteName: string
}

export function useConflictErrors(props: UseConflictErrorsProps): {
  conflicts: ComputedRef<ConflictDisplayData[]>
} {
  const ctx = getCurrentInstance()
  if (!ctx) {
    throw new Error("'ctx' is not defined")
  }

  const { $epb, $router } = ctx.proxy

  function getConflictHref(id: string): string | undefined {
    return props.updateRouteName
      ? $router.resolve({
          name: props.updateRouteName,
          params: { id },
        }).href
      : undefined
  }

  const getConflictDisplayData = (
    conflictingElement: ConflictingElementInfo,
  ): ConflictDisplayData => {
    const conflictingQueries = conflictingElement.queries?.length
      ? ArrayUtils.getIntersection(conflictingElement.queries, props.itemQueries).join(', ')
      : undefined

    const conflictMessage = conflictingQueries
      ? $epb.utils.compileMessage(props.messages.detail, {
          queries: conflictingQueries,
        })
      : ''

    return {
      id: conflictingElement.id,
      name: conflictingElement.name || conflictingElement.id,
      href: getConflictHref(conflictingElement.id),
      queries: conflictingQueries,
      detail: conflictMessage,
    }
  }

  const conflicts = computed<ConflictDisplayData[]>(() => {
    if (!props.conflictApiError || !props.conflictApiError.conflictingElements?.length) {
      return []
    }
    return props.conflictApiError.conflictingElements.map(getConflictDisplayData)
  })

  return { conflicts }
}
